/**
 * The dust's scattered sunlight past single scattering, as one table Q the march reads per lit sample.
 *
 * Per texel (local sun elevation, altitude, view zenith split at the horizon, azimuth from the sun) Q holds, per unit
 * density, the first-order lobe-1 field (exact quadrature over arrival directions), its orders 2 to 8 iterated on the
 * table itself, and the lobe-1 in-scatter of the diffuse field (orders 1 to 6), folded with the gas and dust scattering
 * coefficients. All of it is GPU passes over the table, a chunk at a time, on a copy of the air taken as the build
 * starts; the sun moving never rebuilds it, tau does, once the build in flight has landed.
 */

import * as THREE from 'three';

export interface DustGrid {
  readonly nr: number;
  readonly na: number;
  readonly nb: number;
  readonly nphi: number;
  readonly slices: number;
}

/** The size the CPU model held its bars at; 10 x (32 + 10) x 12 does not. */
export const DUST_GRID: DustGrid = { nr: 16, na: 48, nb: 10, nphi: 24, slices: 24 };

const SLICE_SCALE_DEG = 5;
const SLICE_MIN_DEG = -10;
const SLICE_MAX_DEG = 90;
const BH_ORDERS = 8;
/** X1's quadrature (exact1.mjs): nodes on the two inner zenith segments, the outer one, and per azimuth arc. */
const X1_N1 = 8;
const X1_N3 = 4;
const X1_NP = 8;
const QD_ORDERS = 6;
const COL_W = 256;
const COL_H = 128;
const COL_STEPS = 1024;

function glNodes(n: number): { x: number[]; w: number[] } {
  const x: number[] = [];
  const w: number[] = [];
  for (let i = 1; i <= n; i++) {
    let z = Math.cos(Math.PI * (i - 0.25) / (n + 0.5));
    let pp = 1;
    for (let it = 0; it < 100; it++) {
      let p1 = 1;
      let p2 = 0;
      for (let j = 1; j <= n; j++) {
        const p3 = p2;
        p2 = p1;
        p1 = ((2 * j - 1) * z * p2 - (j - 1) * p3) / j;
      }
      pp = n * (z * p1 - p2) / (z * z - 1);
      const z1 = z;
      z = z1 - p1 / pp;
      if (Math.abs(z - z1) < 1e-15) break;
    }
    x.push((1 - z) / 2);
    w.push(1 / ((1 - z * z) * pp * pp));
  }
  return { x, w };
}

const glArrays = (name: string, n: number): string => {
  const g = glNodes(n);
  const f = (a: number[]): string => a.map((v) => v.toPrecision(9)).join(', ');
  return `const float ${name}X[${n}] = float[${n}](${f(g.x)});\nconst float ${name}W[${n}] = float[${n}](${f(g.w)});\n`;
};

const flt = (v: number): string => (Number.isInteger(v) ? `${v}.0` : `${v}`);

/** The grid's #defines, which every shader that reads Q carries. */
export function dustGlslDefines(g: DustGrid): string {
  const a = SLICE_SCALE_DEG * Math.PI / 180;
  const x0 = Math.asinh(SLICE_MIN_DEG / SLICE_SCALE_DEG);
  const dx = (Math.asinh(SLICE_MAX_DEG / SLICE_SCALE_DEG) - x0) / (g.slices - 1);
  return `#define DQ_NR ${flt(g.nr)}\n#define DQ_NA ${flt(g.na)}\n#define DQ_NB ${flt(g.nb)}\n#define DQ_NP ${flt(g.nphi)}\n`
    + `#define DQ_NS ${flt(g.slices)}\n#define DQ_SA ${a}\n#define DQ_SX0 ${x0}\n#define DQ_SDX ${dx}\n`;
}

/** Q's read: sun slices blended by hand, altitude by hand, zenith and azimuth by the filter. */
export const DUST_LOOK_GLSL = /* glsl */ `
uniform sampler2D uAtmoDustQ;
#define DQ_NM (DQ_NA + DQ_NB)
#define DQ_W (DQ_NP * DQ_NR)
#define DQ_H (DQ_NM * DQ_NS)
float dqMuH(float r) { float k = uAtmoRadii.x / r; return -sqrt(max(1.0 - k * k, 0.0)); }
float dqSliceEl(float k) { return DQ_SA * sinh(DQ_SX0 + k * DQ_SDX); }
vec3 dqLook(sampler2D T, vec3 P, vec3 w, vec3 sun) {
  float rg = uAtmoRadii.x;
  float top = uAtmoRadii.y - rg;
  float r = max(length(P), rg + 2.0);
  vec3 u = P / r;
  float mu = clamp(dot(w, u), -1.0, 1.0);
  float s = clamp(dot(sun, u), -1.0, 1.0);
  float den = sqrt(max((1.0 - mu * mu) * (1.0 - s * s), 1e-30));
  float cf = clamp((dot(w, sun) - mu * s) / den, -1.0, 1.0);
  float af = sqrt(clamp((r - rg - 2.0) / (top - 2.0), 0.0, 1.0)) * (DQ_NR - 1.0);
  float a = min(floor(af), DQ_NR - 2.0);
  float fa = af - a;
  float h = dqMuH(r);
  float bf = mu >= h ? sqrt(clamp((mu - h) / (1.0 - h), 0.0, 1.0)) * (DQ_NA - 1.0)
    : DQ_NA + sqrt(clamp((h - mu) / (1.0 + h), 0.0, 1.0)) * (DQ_NB - 1.0);
  float cfl = sqrt(acos(cf) / ATMO_PI) * (DQ_NP - 1.0);
  float sf = clamp((asinh(asin(s) / DQ_SA) - DQ_SX0) / DQ_SDX, 0.0, DQ_NS - 1.0);
  float s0 = min(floor(sf), DQ_NS - 2.0);
  float fs = sf - s0;
  float x0 = (a * DQ_NP + cfl + 0.5) / DQ_W;
  float x1 = x0 + DQ_NP / DQ_W;
  float y0 = (s0 * DQ_NM + bf + 0.5) / DQ_H;
  float y1 = y0 + DQ_NM / DQ_H;
  vec3 lo = mix(texture2D(T, vec2(x0, y0)).rgb, texture2D(T, vec2(x1, y0)).rgb, fa);
  vec3 hi = mix(texture2D(T, vec2(x0, y1)).rgb, texture2D(T, vec2(x1, y1)).rgb, fa);
  return mix(lo, hi, fs);
}
// Q per unit density is near 1e-6 per metre, under half float's normal range, where the orders added into it round
// away; it is stored times DQ_K, and over dqNorm, the peak round the sun that sun slices 11 deg apart cannot lerp.
#define DQ_K 65536.0
vec3 dqNorm(float c) {
  vec3 g = uAtmoMieG1 * uAtmoMieG1;
  return (vec3(1.0) + (1.0 - g * g) / pow(max(1.0 + g * g - 2.0 * g * c, vec3(1e-4)), vec3(1.5))) / (4.0 * ATMO_PI);
}
vec3 dqQ(vec3 P, vec3 w, vec3 sun) { return dqLook(uAtmoDustQ, P, w, sun) * dqNorm(dot(w, sun)) * (1.0 / DQ_K); }
`;

const BUILD_GLSL = /* glsl */ `
${glArrays('DQ_G4', 4)}${glArrays('DQ_G12', 12)}${glArrays('DQ_G24', 24)}
${glArrays('DQ_GA', X1_N1)}${glArrays('DQ_GB', X1_N3)}${glArrays('DQ_GP', X1_NP)}
uniform sampler2D uDqCol;
uniform vec2 uDqColSize;
uniform sampler2D uDqIn;
uniform sampler2D uDqQs;
uniform sampler2D uDqNs;
uniform float uDqMode;
uniform float uDqGround;
layout(location = 0) out highp vec4 oF;
#ifdef DQ_MRT
layout(location = 1) out highp vec4 oQs;
layout(location = 2) out highp vec4 oNs;
#endif
#define DQ_W0 2000.0

float dqCol(float r, float mu) {
  vec2 x = atmoTransmittanceParamsToUv(clamp(r, uAtmoRadii.x, uAtmoRadii.y), mu);
  return texture2D(uDqCol, vec2(atmoUnitToTexCoord(x.x, uDqColSize.x), atmoUnitToTexCoord(x.y, uDqColSize.y))).r;
}
vec3 dqKx() { return uAtmoBetaR + uAtmoBetaM * uAtmoMieExt; }
vec3 dqSsaBeta() { return uAtmoMieSsa * uAtmoBetaM * uAtmoMieExt; }
float dqRayPhase(float c) { return 3.0 / (16.0 * ATMO_PI) * (1.0 + c * c); }

struct DqTexel { float r; float mu; vec3 x; vec3 w; vec3 sun; };
DqTexel dqTexel() {
  DqTexel t;
  float col = floor(gl_FragCoord.x);
  float row = floor(gl_FragCoord.y);
  float a = floor(col / DQ_NP);
  float c = col - a * DQ_NP;
  float sl = floor(row / DQ_NM);
  float m = row - sl * DQ_NM;
  float rg = uAtmoRadii.x;
  float top = uAtmoRadii.y - rg;
  float ar = a / (DQ_NR - 1.0);
  t.r = rg + 2.0 + (top - 2.0) * ar * ar;
  float h = dqMuH(t.r);
  if (m < DQ_NA) {
    float y = m / (DQ_NA - 1.0);
    t.mu = min(h + (1.0 - h) * y * y + 1e-7, 1.0);
  } else {
    float z = (m - DQ_NA) / (DQ_NB - 1.0);
    t.mu = max(h - (1.0 + h) * z * z - 1e-7, -1.0);
  }
  float cp = c / (DQ_NP - 1.0);
  float ph = ATMO_PI * cp * cp;
  float e = dqSliceEl(sl);
  float st = sqrt(max(1.0 - t.mu * t.mu, 0.0));
  t.x = vec3(0.0, t.r, 0.0);
  t.sun = vec3(cos(e), sin(e), 0.0);
  t.w = vec3(st * cos(ph), t.mu, -st * sin(ph));
  return t;
}

/** A line from radius r at cosine mu: where it ends, whether on the ground, and the column it starts with. */
void dqLine(float r, float mu, out float smax, out bool hitG, out float iq) {
  float h = dqMuH(r);
  float rt = uAtmoRadii.y;
  hitG = mu < 0.0 && mu * mu >= h * h;
  smax = hitG ? r * (-mu - sqrt(max(mu * mu - h * h, 0.0)))
    : -r * mu + sqrt(max((rt - r) * (rt + r) + r * r * mu * mu, 0.0));
  iq = hitG ? dqCol(r, -mu) : dqCol(r, mu);
}

// Segment k of [a, b], widths doubling from both ends to the midpoint (exact1.mjs lineNodes).
bool dqSegment(float a, float b, int k, out float s0, out float s1) {
  float m = 0.5 * (a + b);
  float n = ceil(log2(max((m - a) / DQ_W0, 0.0) + 1.0) - 1e-6);
  float j = float(k);
  if (j >= 2.0 * n) return false;
  if (j < n) {
    s0 = a + DQ_W0 * (exp2(j) - 1.0);
    s1 = min(a + DQ_W0 * (exp2(j + 1.0) - 1.0), m);
  } else {
    float i = j - n;
    s1 = b - DQ_W0 * (exp2(i) - 1.0);
    s0 = max(b - DQ_W0 * (exp2(i + 1.0) - 1.0), m);
  }
  return s1 > s0;
}

/**
 * Along x + w s, the density-weighted sum of exp(-kx J) times f: J the column back to x. f is the sun's
 * transmittance at the node (first order) or the table uDqIn there (a march).
 */
vec3 dqMarch(vec3 x, float r, vec3 w, vec3 sun, bool first) {
  float mu = dot(x, w) / r;
  float smax; bool hitG; float iq;
  dqLine(r, mu, smax, hitG, iq);
  if (!(smax > 0.0)) return vec3(0.0);
  float rg = uAtmoRadii.x;
  vec3 kx = dqKx();
  float tt = -r * mu;
  bool split = !hitG && tt > 0.0 && tt < smax;
  vec3 sum = vec3(0.0);
  for (int iv = 0; iv < 2; iv++) {
    if (iv == 1 && !split) break;
    float a = iv == 0 ? 0.0 : tt;
    float b = (iv == 0 && split) ? tt : smax;
    for (int k = 0; k < 64; k++) {
      float s0; float s1;
      if (!dqSegment(a, b, k, s0, s1)) break;
      float len = s1 - s0;
      for (int g = 0; g < 4; g++) {
        float s = s0 + len * DQ_G4X[g];
        vec3 P = x + w * s;
        float rP = max(length(P), rg);
        float muP = dot(P, w) / rP;
        float jc = max(hitG ? dqCol(rP, -muP) - iq : iq - dqCol(rP, muP), 0.0);
        float e = len * DQ_G4W[g] * exp(-(rP - rg) / uAtmoScaleH.y);
        if (first) {
          float muS = dot(P, sun) / rP;
          if (muS < dqMuH(rP)) continue;
          sum += e * exp(-kx * (jc + dqCol(rP, muS)));
        } else {
          sum += e * exp(-kx * jc) * dqLook(uDqIn, P, w, sun);
        }
      }
    }
  }
  return sum;
}

#ifdef DQ_X1
/** exact1.mjs: first-order radiance per unit irradiance over arrival directions, both cones (sun, view). */
void dqExact1(vec3 q, vec3 dir, vec3 sun, out vec3 S, out vec3 Sf, out vec3 R) {
  S = vec3(0.0); Sf = vec3(0.0); R = vec3(0.0);
  float rg = uAtmoRadii.x;
  float rq = length(q);
  vec3 up = q / rq;
  float k = rg / rq;
  float sinDip = sqrt(max(1.0 - k * k, 0.0));
  float dip = asin(sinDip);
  vec3 sig = dqSsaBeta() * uAtmoMieAlpha;
  for (int cone = 0; cone < 2; cone++) {
    vec3 c0 = cone == 0 ? sun : dir;
    vec3 other = cone == 0 ? dir : sun;
    float se = dot(c0, up);
    vec3 e1 = up - se * c0;
    float ce = length(e1);
    if (ce < 1e-6) {
      e1 = abs(c0.x) < 0.9 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 0.0, 1.0);
      e1 -= dot(e1, c0) * c0;
      ce = 0.0;
    }
    e1 = normalize(e1);
    vec3 e2 = cross(c0, e1);
    float eps0 = asin(clamp(se, -1.0, 1.0));
    float ta = abs(eps0 + dip);
    float tb = ATMO_PI - abs(eps0 - dip);
    float br1 = sqrt(ta / ATMO_PI);
    float br2 = sqrt(max(tb, ta) / ATMO_PI);
    for (int seg = 0; seg < 3; seg++) {
      float u0 = seg == 0 ? 0.0 : (seg == 1 ? br1 : br2);
      float u1 = seg == 0 ? br1 : (seg == 1 ? br2 : 1.0);
      if (u1 <= u0) continue;
      int n = seg == 2 ? ${X1_N3} : ${X1_N1};
      for (int a2 = 0; a2 < ${X1_N1}; a2++) {
        if (a2 >= n) break;
        float gx = seg == 2 ? DQ_GBX[a2 < ${X1_N3} ? a2 : 0] : DQ_GAX[a2];
        float gw = seg == 2 ? DQ_GBW[a2 < ${X1_N3} ? a2 : 0] : DQ_GAW[a2];
        float u = u0 + (u1 - u0) * gx;
        float th = ATMO_PI * u * u;
        float ct = cos(th);
        float st = sin(th);
        float wth = (u1 - u0) * gw * 2.0 * ATMO_PI * u * st;
        float cs = st * ce > 1e-12 ? (-sinDip - ct * se) / (st * ce) : 2.0;
        bool arcs = cs > -1.0 && cs < 1.0;
        float f = arcs ? acos(cs) : 0.0;
        for (int b2 = 0; b2 < ${2 * X1_NP}; b2++) {
          float ph;
          float wph;
          if (arcs) {
            int arc = b2 / ${X1_NP};
            int i6 = b2 - arc * ${X1_NP};
            float f0 = arc == 0 ? -f : f;
            float f1 = arc == 0 ? f : 2.0 * ATMO_PI - f;
            ph = f0 + (f1 - f0) * DQ_GPX[i6];
            wph = (f1 - f0) * DQ_GPW[i6];
          } else {
            ph = 2.0 * ATMO_PI * (float(b2) + 0.5) / ${2 * X1_NP}.0;
            wph = 2.0 * ATMO_PI / ${2 * X1_NP}.0;
          }
          vec3 w = ct * c0 + st * (cos(ph) * e1 + sin(ph) * e2);
          float cOther = dot(w, other);
          float A = 1.0 / ((1.0 - ct + 0.003) * (1.0 - ct + 0.003));
          float B = 1.0 / ((1.0 - cOther + 0.003) * (1.0 - cOther + 0.003));
          float W = wth * wph * A / (A + B);
          vec3 G = sig * dqMarch(q, rq, w, sun, true);
          float cs1 = cone == 0 ? ct : cOther;
          float cv = cone == 0 ? cOther : ct;
          vec3 L1 = atmoHenyeyGreenstein(uAtmoMieG1, cs1) * G * W;
          vec3 h1 = uAtmoMieAlpha * atmoHenyeyGreenstein(uAtmoMieG1, cv);
          Sf += h1 * L1;
          S += (h1 + (vec3(1.0) - uAtmoMieAlpha) * atmoHenyeyGreenstein(vec3(uAtmoMieG2), cv)) * L1;
          R += dqRayPhase(cv) * L1;
        }
      }
    }
  }
}
#endif
`;

const COL_FRAG = /* glsl */ `
void main() {
  vec2 x = (gl_FragCoord.xy - 0.5) / (uDqColSize - 1.0);
  float r; float mu;
  atmoTransmittanceUvToParams(x, r, mu);
  float rg = uAtmoRadii.x;
  float dx = atmoDistanceToTop(r, mu) / float(${COL_STEPS});
  float sum = 0.0;
  for (int i = 0; i < ${COL_STEPS}; i++) {
    float t = (float(i) + 0.5) * dx;
    float ri = sqrt(max(t * t + 2.0 * r * mu * t + r * r, rg * rg));
    sum += exp(-(ri - rg) / uAtmoScaleH.y);
  }
  oF = vec4(sum * dx, 0.0, 0.0, 1.0);
}
`;

const X1_FRAG = /* glsl */ `
void main() {
  DqTexel t = dqTexel();
  vec3 S; vec3 Sf; vec3 R;
  dqExact1(t.x, t.w, t.sun, S, Sf, R);
  vec3 sb = dqSsaBeta();
  oF = vec4(Sf / uAtmoMieAlpha, 1.0);
  oQs = vec4(DQ_K * (uAtmoBetaR * R + sb * S) / dqNorm(dot(t.w, t.sun)), 1.0);
  oNs = vec4(DQ_K * (uAtmoBetaR * R + sb * (S - Sf)), 1.0);
}
`;

const MARCH_FRAG = /* glsl */ `
void main() {
  DqTexel t = dqTexel();
  vec3 L = dqSsaBeta() * uAtmoMieAlpha * dqMarch(t.x, t.r, t.w, t.sun, false);
  float smax; bool hitG; float iq;
  dqLine(t.r, t.mu, smax, hitG, iq);
  if (uDqGround > 0.5 && hitG) {
    float rg = uAtmoRadii.x;
    vec3 P = t.x + t.w * smax;
    float rP = length(P);
    float nL = clamp(dot(P, t.sun) / rP, 0.0, 1.0);
    if (nL > 0.0) {
      float jc = max(dqCol(rg, -dot(P, t.w) / rP) - iq, 0.0);
      L += uAtmoGroundAlbedo / ATMO_PI * nL * atmoSunTransmittanceScaled(rg, nL) * exp(-dqKx() * jc);
    }
  }
  oF = vec4(L, 1.0);
}
`;

/** uDqMode 0: orders 2+ of the beam (lobe 1, gas, lobe 2 into Q). 1: the diffuse field's lobe-1 in-scatter. */
const conv = (nu: number, npsi: number): string => /* glsl */ `
void main() {
  DqTexel t = dqTexel();
  vec3 w = t.w;
  vec3 a0 = abs(w.x) < 0.9 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0);
  vec3 t1 = normalize(cross(w, a0));
  vec3 t2 = cross(w, t1);
  vec3 q1 = vec3(0.0); vec3 qr = vec3(0.0); vec3 q2 = vec3(0.0);
  for (int iu = 0; iu < ${nu}; iu++) {
    float u = DQ_G${nu}X[iu];
    float th = ATMO_PI * u * u;
    float ct = cos(th);
    float st = sin(th);
    float dW = DQ_G${nu}W[iu] * 2.0 * ATMO_PI * u * st * 2.0 * ATMO_PI / float(${npsi});
    vec3 sum = vec3(0.0);
    for (int ip = 0; ip < ${npsi}; ip++) {
      float ps = 2.0 * ATMO_PI * (float(ip) + 0.5) / float(${npsi});
      sum += dqLook(uDqIn, t.x, ct * w + st * (cos(ps) * t1 + sin(ps) * t2), t.sun);
    }
    q1 += atmoHenyeyGreenstein(uAtmoMieG1, ct) * dW * sum;
    qr += dqRayPhase(ct) * dW * sum;
    q2 += atmoHenyeyGreenstein(vec3(uAtmoMieG2), ct) * dW * sum;
  }
  ivec2 at = ivec2(gl_FragCoord.xy);
  vec3 qs = texelFetch(uDqQs, at, 0).rgb;
  vec3 ns = texelFetch(uDqNs, at, 0).rgb;
  vec3 sb = dqSsaBeta();
  vec3 al = uAtmoMieAlpha;
  vec3 kq = DQ_K / dqNorm(dot(w, t.sun));
  oF = vec4(q1, 1.0);
  if (uDqMode < 0.5) {
    oQs = vec4(qs + kq * (uAtmoBetaR * qr + sb * (al * q1 + (vec3(1.0) - al) * q2)), 1.0);
    oNs = vec4(ns + DQ_K * (uAtmoBetaR * qr + sb * (vec3(1.0) - al) * q2), 1.0);
  } else {
    oQs = vec4(qs + kq * sb * al * q1, 1.0);
    oNs = vec4(ns, 1.0);
  }
}
`;

/** The diffuse sources a view sample has at the texel, over lobe 1's scattering coefficient (diff2.mjs srcRange). */
const SRC_FRAG = /* glsl */ `
void main() {
  DqTexel t = dqTexel();
  ivec2 at = ivec2(gl_FragCoord.xy);
  vec3 qs = texelFetch(uDqQs, at, 0).rgb;
  vec3 ns = texelFetch(uDqNs, at, 0).rgb;
  float muS = t.sun.y;
  vec3 sb = dqSsaBeta();
  vec3 al = uAtmoMieAlpha;
  float c = dot(t.w, t.sun);
  vec3 src = atmoMultiScatter(t.r, muS) * (uAtmoBetaR + sb * (vec3(1.0) - al));
  if (muS >= dqMuH(t.r)) {
    vec3 phM = atmoMiePhase(c) - al * atmoHenyeyGreenstein(uAtmoMieG1, c);
    src += atmoTransmittanceToTop(t.r, muS) * (uAtmoBetaR * dqRayPhase(c) + sb * phM) + ns * (1.0 / DQ_K);
  }
  oF = vec4(src / (sb * al), 1.0);
  oQs = vec4(qs, 1.0);
  oNs = vec4(ns, 1.0);
}
`;

const COPY_FRAG = /* glsl */ `
uniform sampler2D uDqSrc;
layout(location = 0) out highp vec4 oF;
void main() { oF = texelFetch(uDqSrc, ivec2(gl_FragCoord.xy), 0); }
`;

type PassKind = 'col' | 'x1' | 'march' | 'conv-bh' | 'conv-qd' | 'src';
interface Step {
  /** 'snap' copies the tables the build reads; 'final' copies Q out of its MRT group. */
  readonly kind: PassKind | 'snap' | 'final';
  /** Texel rows this draw covers, [first, end). */
  readonly y0: number;
  readonly y1: number;
  readonly ground?: boolean;
  /** The MRT group written (conv, src, x1), or the march's input group. */
  readonly group: 0 | 1;
}

/** Sun slices per draw; X1 and the column table go by texel rows. */
type ChunkSize = Record<Exclude<PassKind, 'x1' | 'col'>, number> & { readonly x1Rows: number; readonly colRows: number };
/** The first build, in about 125 draws: the sky has no Q to draw meanwhile, and a mount's first frames stall anyway. */
const FIRST_CHUNKS: ChunkSize = { 'march': 8, 'conv-bh': 4, 'conv-qd': 8, 'src': 24, x1Rows: 58, colRows: 128 };
/**
 * A rebuild, in 470 draws while the last Q is drawn. An X1 draw costs one texel's quadrature until the rows fill the
 * GPU: 1 and 8 rows read the same 30-40 ms median here (readback-synced, GPU shared), 16 rows 35-59 ms, 29 rows 53 ms.
 */
const REBUILD_CHUNKS: ChunkSize = { 'march': 4, 'conv-bh': 1, 'conv-qd': 4, 'src': 6, x1Rows: 8, colRows: 16 };
/** How long a landed build keeps its scratch (about 30 MB) for the next: a tau step at 1 HR/S comes about once a minute. */
const WORK_KEEP_MS = 120_000;
/** The live tables a build reads, copied when it starts so a rebuild of them mid-build cannot reach it. */
const SNAPPED = ['uAtmoTransmittance', 'uAtmoMultiScatter'] as const;

export interface DustTablesHandle {
  /** Q, a 1x1 zero until the first build lands. */
  readonly texture: THREE.Texture;
  /** Draws still owed. */
  pending(): number;
  /** The air changed: build again, after the build in flight (on the air it started with) lands. */
  invalidate(): void;
  /** Frees the build's scratch once no build has landed for `WORK_KEEP_MS`. */
  trim(nowMs: number): void;
  /** One draw of the build. The caller owns the renderer's target, viewport and scissor around it. */
  step(canvasViewport: boolean): void;
  dispose(): void;
}

export interface DustTablesConfig {
  readonly renderer: THREE.WebGLRenderer;
  readonly uniforms: Record<string, THREE.IUniform>;
  /** ATMO_COMMON_GLSL with the row's head, which carries dustGlslDefines and DUST_LOOK_GLSL. */
  readonly common: string;
  readonly vertexShader: string;
  readonly grid: DustGrid;
  readonly name: string;
  readonly onTexture: (t: THREE.Texture) => void;
  readonly stop?: 'x1' | 'bh';
}

export function createDustTables(cfg: DustTablesConfig): DustTablesHandle {
  const g = cfg.grid;
  const nm = g.na + g.nb;
  const W = g.nphi * g.nr;
  const H = nm * g.slices;
  const own: Record<string, THREE.IUniform> = {
    uDqCol: { value: null },
    uDqColSize: { value: new THREE.Vector2(COL_W, COL_H) },
    uDqIn: { value: null },
    uDqQs: { value: null },
    uDqNs: { value: null },
    uDqMode: { value: 0 },
    uDqGround: { value: 0 },
  };
  // The build reads its own copy of the air, taken when it starts: a tau step mid-build changes the live one.
  const snap: Record<string, THREE.IUniform> = {};
  for (const [k, u] of Object.entries(cfg.uniforms)) {
    const v = u.value;
    if (Array.isArray(v)) throw new Error(`dust build: array uniform ${k} would be shared live, not snapshotted`);
    snap[k] = { value: v !== null && typeof v === 'object' && !v.isTexture && typeof v.clone === 'function' ? v.clone() : v };
  }
  const uniforms = { ...snap, ...own };
  const material = (frag: string, defines: string, mrt: boolean): THREE.ShaderMaterial => new THREE.ShaderMaterial({
    uniforms,
    glslVersion: THREE.GLSL3,
    vertexShader: cfg.vertexShader,
    fragmentShader: `${mrt ? '#define DQ_MRT\n' : ''}${defines}${cfg.common}\n${BUILD_GLSL}\n${frag}`,
    depthTest: false,
    depthWrite: false,
  });
  const materials: Record<PassKind, THREE.ShaderMaterial> = {
    'col': material(COL_FRAG, '', false),
    'x1': material(X1_FRAG, '#define DQ_X1\n', true),
    'march': material(MARCH_FRAG, '', false),
    'conv-bh': material(conv(24, 32), '', true),
    'conv-qd': material(conv(12, 16), '', true),
    'src': material(SRC_FRAG, '', true),
  };
  const copyUniforms = { uDqSrc: { value: null as THREE.Texture | null } };
  const copyMaterial = new THREE.ShaderMaterial({
    uniforms: copyUniforms,
    glslVersion: THREE.GLSL3,
    vertexShader: cfg.vertexShader,
    fragmentShader: COPY_FRAG,
    depthTest: false,
    depthWrite: false,
  });
  const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), materials.col);
  quad.frustumCulled = false;
  const scene = new THREE.Scene();
  scene.add(quad);
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  const placeholder = new THREE.DataTexture(new Uint16Array(4), 1, 1, THREE.RGBAFormat, THREE.HalfFloatType);
  placeholder.needsUpdate = true;
  let final: THREE.WebGLRenderTarget | null = null;
  let work: {
    col: THREE.WebGLRenderTarget;
    groups: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget];
    line: THREE.WebGLRenderTarget;
    snapped: THREE.WebGLRenderTarget[];
  } | null = null;

  const target = (w: number, h: number, count: number, type: THREE.TextureDataType): THREE.WebGLRenderTarget => {
    const t = new THREE.WebGLRenderTarget(w, h, {
      type, format: THREE.RGBAFormat, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
      wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping, depthBuffer: false, stencilBuffer: false,
      generateMipmaps: false, count,
    });
    for (const tex of t.textures) tex.colorSpace = THREE.NoColorSpace;
    t.texture.name = cfg.name;
    return t;
  };

  const makePlan = (size: ChunkSize): Step[] => {
    const plan: Step[] = [];
    const chunks = (kind: Exclude<PassKind, 'col'>, group: 0 | 1, ground = false): void => {
      const rows = kind === 'x1' ? size.x1Rows : size[kind] * nm;
      for (let y = 0; y < H; y += rows) plan.push({ kind, y0: y, y1: Math.min(y + rows, H), group, ground });
    };
    plan.push({ kind: 'snap', y0: 0, y1: 0, group: 0 });
    for (let y = 0; y < COL_H; y += size.colRows) plan.push({ kind: 'col', y0: y, y1: y + size.colRows, group: 0 });
    let cur: 0 | 1 = 0;
    chunks('x1', cur);
    const flip = (): 0 | 1 => (cur = cur === 0 ? 1 : 0);
    for (let j = 2; j <= BH_ORDERS; j++) {
      chunks('march', cur);
      chunks('conv-bh', flip());
    }
    chunks('src', flip());
    chunks('march', cur, true);
    for (let n = 0; n < QD_ORDERS; n++) {
      chunks('conv-qd', flip());
      if (n < QD_ORDERS - 1) chunks('march', cur);
    }
    const stopKind = cfg.stop === 'x1' ? 'x1' : cfg.stop === 'bh' ? 'conv-bh' : null;
    if (stopKind) plan.length = plan.map((s) => s.kind).lastIndexOf(stopKind) + 1;
    plan.push({ kind: 'final', y0: 0, y1: H, group: plan[plan.length - 1]!.group });
    return plan;
  };
  const plans = { first: makePlan(FIRST_CHUNKS), again: makePlan(REBUILD_CHUNKS) };
  /** The build in flight's plan: a first build has no Q to keep drawing, and lands in as few frames as it can. */
  let plan = plans.first;
  let next = 0;
  /** The air changed while a build was in flight: build again once it lands. */
  let again = false;

  const freeWork = (): void => {
    if (!work) return;
    work.col.dispose();
    work.groups[0].dispose();
    work.groups[1].dispose();
    work.line.dispose();
    for (const t of work.snapped) t.dispose();
    work = null;
  };
  let landedAt = 0;

  const draw = (out: THREE.WebGLRenderTarget, m: THREE.ShaderMaterial, y0: number, h: number, canvasViewport: boolean): void => {
    const r = cfg.renderer;
    quad.material = m;
    out.viewport.set(0, 0, out.width, out.height);
    out.scissor.set(0, y0, out.width, h);
    out.scissorTest = true;
    r.setRenderTarget(out);
    if (canvasViewport) {
      r.setViewport(0, 0, out.width, out.height);
      r.setScissor(0, y0, out.width, h);
      r.setScissorTest(true);
    }
    r.render(scene, camera);
    out.scissorTest = false;
    r.setScissorTest(false);
  };
  const copy = (from: THREE.Texture, to: THREE.WebGLRenderTarget, canvasViewport: boolean): void => {
    copyUniforms.uDqSrc.value = from;
    draw(to, copyMaterial, 0, to.height, canvasViewport);
  };

  return {
    get texture() { return final ? final.texture : placeholder; },
    pending: () => plan.length - next + (again ? plans.again.length : 0),
    invalidate(): void {
      if (next > 0 && next < plan.length) again = true;
      else next = 0;
    },
    trim(nowMs): void {
      if (work && next >= plan.length && nowMs - landedAt > WORK_KEEP_MS) freeWork();
    },
    step(canvasViewport): void {
      if (next >= plan.length) return;
      if (next === 0) plan = final ? plans.again : plans.first;
      const st = plan[next]!;
      if (!work) {
        work = {
          col: target(COL_W, COL_H, 1, THREE.FloatType),
          groups: [target(W, H, 3, THREE.HalfFloatType), target(W, H, 3, THREE.HalfFloatType)],
          line: target(W, H, 1, THREE.HalfFloatType),
          snapped: [],
        };
      }
      next++;
      if (st.kind === 'snap') {
        for (const [k, u] of Object.entries(snap)) {
          const v = cfg.uniforms[k]!.value;
          if (v !== null && typeof v === 'object' && !v.isTexture && typeof v.copy === 'function') {
            if (typeof u.value?.copy === 'function') u.value.copy(v);
            else u.value = typeof v.clone === 'function' ? v.clone() : v;
          } else u.value = v;
        }
        SNAPPED.forEach((k, i) => {
          const live = cfg.uniforms[k]!.value as THREE.Texture;
          const img = live.image as { width: number; height: number };
          const t = work!.snapped[i] ?? (work!.snapped[i] = target(img.width, img.height, 1, live.type));
          copy(live, t, canvasViewport);
          snap[k]!.value = t.texture;
        });
        return;
      }
      if (st.kind === 'final') {
        // Q alone outlives the build: its MRT group's other two attachments, the line field and the column table go.
        // A first draw into a new target costs a frame or more here, so a build writes over the last one's Q, and
        // keeps its scratch for the next (`trim`).
        final ??= target(W, H, 1, THREE.HalfFloatType);
        copy(work.groups[st.group].textures[1]!, final, canvasViewport);
        landedAt = performance.now();
        cfg.onTexture(final.texture);
        if (again) {
          again = false;
          next = 0;
        }
        return;
      }
      const other = work.groups[st.group === 0 ? 1 : 0];
      own['uDqCol']!.value = work.col.texture;
      own['uDqGround']!.value = st.ground ? 1 : 0;
      own['uDqMode']!.value = st.kind === 'conv-qd' ? 1 : 0;
      let out: THREE.WebGLRenderTarget;
      if (st.kind === 'col') {
        out = work.col;
      } else if (st.kind === 'march') {
        own['uDqIn']!.value = work.groups[st.group].textures[0];
        out = work.line;
      } else {
        own['uDqIn']!.value = work.line.texture;
        own['uDqQs']!.value = other.textures[1];
        own['uDqNs']!.value = other.textures[2];
        out = work.groups[st.group];
      }
      draw(out, materials[st.kind], st.y0, st.y1 - st.y0, canvasViewport);
    },
    dispose(): void {
      freeWork();
      final?.dispose();
      placeholder.dispose();
      for (const m of Object.values(materials)) m.dispose();
      copyMaterial.dispose();
      quad.geometry.dispose();
    },
  };
}
